import type { SelectMultipleOnSelectAll } from '@/components_deprecated'
import {
  DateRangePicker,
  MonthPicker,
  Select,
  Toggle,
} from '@/components_deprecated'
import { SelectMultipleUsers } from '@/components_deprecated/Select/SelectMultipleUsers'
import { DropdownNames } from '@/components_deprecated/Select/types'
import { UserOrderBy } from '@/modules/activities'
import { useActivityExportMutations } from '@/modules/activities/mutations.hooks'
import { ExportFormat, ExportOption, ExportType } from '@/modules/exports'
import {
  useExportFormatOptions,
  useExportOptions,
} from '@/modules/exports/hooks'
import { SIMPLE_USERS_QUERY } from '@/modules/users/queries'
import { handleDateRangeChange } from '@/utils/date/handleDateRangeChange'
import { notEmptyArrayValidation } from '@/utils/forms'
import { getNodes } from '@/utils/graphql'
import { useCompany, useUser } from '@/utils/hooks'
import { isInvalidDate } from '@/utils/time'
import { useQuery } from '@apollo/client'
import { ComponentSwitch, Icon } from '@ur-solutions/react-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { endOfMonth, formatISO, startOfMonth } from 'date-fns'
import React, { useCallback, useEffect, useMemo } from 'react'
import { isMobile } from 'react-device-detect'
import styled from 'styled-components'
import type { FormButton } from '../common'
import { Field, Form, Section } from '../common'

const SelectMultipleUsersWrapper = styled.div`
  position: relative;
  min-height: 54px;
`

interface ArbeidstilsynetExportForm {
  month: Date
  dateStart: Date
  dateEnd: Date
  exportOption: ExportOption
  exportFormat: ExportFormat
  useDateRange: boolean
  users: string[]
}

export const ExportArbeidstilsynetCard: React.FC = () => {
  const translations = useTranslate({
    period: 'common.period',
    exportMethod: 'common.export-method',
    exportFormat: 'common.format',
    export: 'common.export',
    selectUsers: 'common.select-users',
    selectUsersError: 'activities.errors.select-multiple-users',
    downloadOnlySupportsPdf: 'settings.download-only-supports-pdf',
  })

  const me = useUser()
  const company = useCompany()
  const { exportActivitiesMass, loading } = useActivityExportMutations()
  const { data: companyUsersData } = useQuery(SIMPLE_USERS_QUERY, {
    variables: {
      first: 10000,
      q: '',
      orderBy: 'lastName',
      dropdown: DropdownNames.ACTIVITIES,
    },
    fetchPolicy: 'cache-and-network',
  })

  const {
    formValues: form,
    formErrors: errors,
    submitHandler: submit,
    formChangeHandler: handler,
    formValid,
    updateForm,
  } = useForm<ArbeidstilsynetExportForm>({
    values: {
      month: new Date(),
      dateStart: new Date(),
      dateEnd: new Date(),
      exportOption: ExportOption.SAVE,
      exportFormat: ExportFormat.PDF,
      useDateRange: false,
      users: [],
    },
    validators: {
      users: value =>
        notEmptyArrayValidation(value) ? translations.selectUsersError : null,
    },
    config: {
      initAsInvalid: true,
      storage: {
        storeFormState: true,
        retrieveFormStateOnMount: true,
        storeFormStateName: `Export-arbeidstilsynet-form-${company.id}`,
        transformValues: {
          month: value =>
            typeof value === 'string'
              ? isInvalidDate(new Date(value), new Date())
              : undefined,
          dateStart: value =>
            typeof value === 'string'
              ? isInvalidDate(new Date(value), new Date())
              : undefined,
          dateEnd: value =>
            typeof value === 'string'
              ? isInvalidDate(new Date(value), new Date())
              : undefined,
          exportFormat: value => value as ExportFormat,
          exportOption: value => value as ExportOption,
          useDateRange: value => value as boolean,
          users: value =>
            Array.isArray(value)
              ? value.filter((userId): userId is string => typeof userId === 'string')
              : [],
        },
      },
    },
  })

  const companyUserIds = useMemo(
    () => new Set(getNodes(companyUsersData?.allUsers).map(user => user.id)),
    [companyUsersData?.allUsers]
  )

  useEffect(() => {
    if (companyUserIds.size === 0 || form.users.length === 0) return

    const companyUsers = form.users.filter(userId => companyUserIds.has(userId))
    if (companyUsers.length === form.users.length) return

    updateForm({ users: companyUsers })
  }, [companyUserIds, form.users, updateForm])

  const getDateInterval = useCallback(
    (startDate: Date, endDate?: Date): [string, string] => {
      if (form.useDateRange) {
        return [formatISO(startDate), formatISO(endDate || new Date())]
      }

      return [
        formatISO(startOfMonth(startDate)),
        formatISO(endOfMonth(startDate)),
      ]
    },
    [form.useDateRange]
  )

  const handleExport = useCallback(
    (values: ArbeidstilsynetExportForm) => {
      const date = values.useDateRange
        ? values.dateStart
        : startOfMonth(values.month)
      const [dateStart, dateEnd] = getDateInterval(date, values.dateEnd)

      void exportActivitiesMass({
        variables: {
          exportType: ExportType.MASS_ARBEIDSTILSYNET,
          exportFormat: values.exportFormat,
          user: me.id,
          users: values.users,
          company: company.id,
          dateStart,
          dateEnd,
          orderBy: UserOrderBy.FAMILY_NAME,
          includeAbsences: false,
          exportOption: values.exportOption,
          weekly: false,
          leaderGroups: [],
        },
      })
    },
    [company.id, exportActivitiesMass, getDateInterval, me.id]
  )

  const exportOptions = useExportOptions(true)
  const excludeExcel = form.exportOption === ExportOption.DOWNLOAD
  const exportFormats = useExportFormatOptions(
    excludeExcel
      ? [
          ExportFormat.EXCEL,
          ExportFormat.EXCEL_FLAT,
          ExportFormat.PDF_SALARY,
          ExportFormat.POWEROFFICE,
          ExportFormat.TRIPLETEX,
          ExportFormat.TWENTYFOUR_SEVEN_OFFICE,
          ExportFormat.VISMA,
          ExportFormat.VISMA_NEW,
        ]
      : [
          ExportFormat.EXCEL_FLAT,
          ExportFormat.PDF_SALARY,
          ExportFormat.POWEROFFICE,
          ExportFormat.TRIPLETEX,
          ExportFormat.TWENTYFOUR_SEVEN_OFFICE,
          ExportFormat.VISMA,
          ExportFormat.VISMA_NEW,
        ]
  )

  const selectAllOnChange: SelectMultipleOnSelectAll = (
    options,
    setSelectAll
  ) => {
    return (value: boolean) => {
      if (value) {
        updateForm({ users: options.map(option => option.value) as string[] })
        setSelectAll(true)
      } else {
        updateForm({ users: [] })
        setSelectAll(false)
      }
    }
  }

  const formButtons = useMemo<FormButton[]>(
    () => [
      {
        text: (
          <>
            <Icon
              icon="download"
              type="light"
              color="primary"
              margin="0 0.5ch 0 0"
            />
            {translations.export}
          </>
        ),
        type: 'submit',
        buttonProps: {
          fullWidth: isMobile,
          disabled: loading || !formValid,
        },
        onClick: submit(handleExport),
      },
    ],
    [formValid, handleExport, loading, submit, translations.export]
  )

  return (
    <Section
      header="Eksport Arbeidstilsynet"
      loading={loading}
      actions={
        <Toggle
          value={form.useDateRange}
          thumbBackground="secondary"
          iconMode="svg"
          localOnIconProps={{
            icon: 'calendar-selection',
            fill: form.useDateRange ? 'white' : 'primary',
          }}
          localOffIconProps={{
            icon: 'calendar-month',
            fill: !form.useDateRange ? 'white' : 'primary',
          }}
          onChange={() => handler('useDateRange')(!form.useDateRange)}
        />
      }
    >
      <Form
        grid={{
          columns: !isMobile ? '1fr 2fr 2fr' : '1fr',
          areas: !isMobile
            ? `
              'month month exportOption'
              'user user exportFormat'
            `
            : `
              'month'
              'exportOption'
              'exportFormat'
              'user'
            `,
        }}
        buttons={formButtons}
      >
        <Field area="month">
          <label>{translations.period}</label>
          <ComponentSwitch>
            <ComponentSwitch.Case case={form.useDateRange}>
              <DateRangePicker
                value={[form.dateStart, form.dateEnd]}
                grayed={false}
                onChange={handleDateRangeChange(updateForm)}
              />
            </ComponentSwitch.Case>

            <ComponentSwitch.Case default>
              <MonthPicker
                value={form.month}
                light
                futureYears={0}
                noYearCycle
                onChange={handler('month')}
              />
            </ComponentSwitch.Case>
          </ComponentSwitch>
        </Field>

        <Field area="exportOption">
          <label>{translations.exportMethod}</label>
          <Select
            value={form.exportOption}
            options={exportOptions}
            fullWidth
            height="3.875rem"
            onChange={value => value && handler('exportOption')(value)}
          />
        </Field>

        <Field area="exportFormat">
          <label>{translations.exportFormat}</label>
          <Select
            value={form.exportFormat}
            options={exportFormats}
            disabled={excludeExcel}
            fullWidth
            height="3.875rem"
            onChange={value => value && handler('exportFormat')(value)}
            error={excludeExcel ? translations.downloadOnlySupportsPdf : null}
          />
        </Field>

        <Field area="user">
          <label>{translations.selectUsers}</label>
          <SelectMultipleUsersWrapper>
            <SelectMultipleUsers
              selected={form.users}
              dropdownPageName={DropdownNames.ACTIVITIES}
              grayed={false}
              error={errors.users}
              width="100%"
              minWidth={isMobile ? '0' : '260px'}
              onChange={handler('users')}
              onSelectAll={selectAllOnChange}
              withSelectAll={true}
            />
          </SelectMultipleUsersWrapper>
        </Field>
      </Form>
    </Section>
  )
}
